import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Input,
  Select,
  DatePicker,
  Spin,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Statistic,
  Empty,
  Modal,
  Alert
} from 'antd';
import {
  DownloadOutlined,
  ReloadOutlined,
  FilterOutlined,
  ClearOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import {
  getActivityLogs,
  exportActivityLogsAsJson,
  exportActivityLogsAsCSV,
  deleteActivityLogsByDateRange
} from '../services/auditService';
import {
  ACTIVITY_TYPES,
  ACTIVITY_LABELS,
  ACTIVITY_CATEGORIES
} from '../models/activityTypes';
import './AuditLogPage.css';

dayjs.locale('fr');

const AuditLogPage = () => {
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteStartDate, setDeleteStartDate] = useState(null);
  const [deleteEndDate, setDeleteEndDate] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Filtres
  const [filters, setFilters] = useState({
    userId: '',
    entityType: '',
    category: '',
    activityType: '',
    startDate: null,
    endDate: null,
    searchText: ''
  });

  // Charger les données au montage et lors des changements de filtres
  const fetchActivityLogs = async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * pageSize;

      const result = await getActivityLogs({
        userId: filters.userId || undefined,
        entityType: filters.entityType || undefined,
        category: filters.category || undefined,
        activityType: filters.activityType || undefined,
        startDate: filters.startDate ? filters.startDate.toISOString() : undefined,
        endDate: filters.endDate ? filters.endDate.toISOString() : undefined,
        limit: pageSize,
        offset
      });

      if (result.success) {
        setActivities(result.data);
        setTotalCount(result.count);
      } else {
        message.error('Erreur lors de la récupération des logs');
      }
    } catch (error) {
      message.error('Erreur: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivityLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, filters]);

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
    setCurrentPage(1); // Réinitialiser à la première page
  };

  const handleResetFilters = () => {
    setFilters({
      userId: '',
      entityType: '',
      category: '',
      activityType: '',
      startDate: null,
      endDate: null,
      searchText: ''
    });
    setCurrentPage(1);
  };

  const handleExportJSON = async () => {
    try {
      setLoading(true);
      const json = await exportActivityLogsAsJson();
      downloadFile(json, 'activity-logs.json', 'application/json');
      message.success('Export JSON réussi');
    } catch (error) {
      message.error('Erreur lors de l\'export JSON');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      setLoading(true);
      const csv = await exportActivityLogsAsCSV();
      downloadFile(csv, 'activity-logs.csv', 'text/csv');
      message.success('Export CSV réussi');
    } catch (error) {
      message.error('Erreur lors de l\'export CSV');
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = (content, filename, mimeType) => {
    const element = document.createElement('a');
    element.setAttribute('href', 'data:' + mimeType + ';charset=utf-8,' + encodeURIComponent(content));
    element.setAttribute('download', filename);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleShowDeleteModal = () => {
    setDeleteStartDate(null);
    setDeleteEndDate(null);
    setDeleteModalVisible(true);
  };

  const handleDeleteActivityLogs = async () => {
    if (!deleteStartDate || !deleteEndDate) {
      message.error('Veuillez sélectionner une plage de dates complète');
      return;
    }

    if (deleteStartDate > deleteEndDate) {
      message.error('La date de début doit être antérieure à la date de fin');
      return;
    }

    // Modal de confirmation avec avertissement supplémentaire
    Modal.confirm({
      title: '⚠️ Confirmation - Suppression définitive',
      icon: null,
      content: (
        <div>
          <Alert
            message="ATTENTION: Cette action est irréversible"
            description="Tous les enregistrements du journal d'activité dans cette plage de dates seront supprimés de manière permanente et ne pourront pas être récupérés."
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <p style={{ marginTop: 12 }}>
            <strong>Plage à supprimer:</strong>
          </p>
          <p>
            Du <strong>{deleteStartDate.format('DD/MM/YYYY HH:mm:ss')}</strong> au <strong>{deleteEndDate.format('DD/MM/YYYY HH:mm:ss')}</strong>
          </p>
          <p style={{ color: '#666', fontSize: '12px', marginTop: 12 }}>
            Êtes-vous vraiment sûr de vouloir continuer?
          </p>
        </div>
      ),
      okText: 'Oui, supprimer définitivement',
      cancelText: 'Annuler',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          setDeleteLoading(true);
          const result = await deleteActivityLogsByDateRange(
            deleteStartDate.toISOString(),
            deleteEndDate.toISOString()
          );

          if (result.success) {
            message.success(`${result.deletedCount} enregistrement(s) supprimé(s) avec succès`);
            setDeleteModalVisible(false);
            setDeleteStartDate(null);
            setDeleteEndDate(null);
            // Rafraîchir les données
            fetchActivityLogs();
          } else {
            message.error('Erreur lors de la suppression des logs');
          }
        } catch (error) {
          message.error('Erreur: ' + error.message);
        } finally {
          setDeleteLoading(false);
        }
      }
    });
  };

  const getActivityColor = (activityType) => {
    if (activityType.includes('created')) return 'green';
    if (activityType.includes('updated')) return 'blue';
    if (activityType.includes('deleted') || activityType.includes('cancel')) return 'red';
    if (activityType.includes('failed')) return 'red';
    if (activityType.includes('completed') || activityType.includes('paid')) return 'green';
    return 'default';
  };

  const columns = [
    {
      title: 'Date/Heure',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (timestamp) => dayjs(timestamp).format('DD/MM/YYYY HH:mm:ss'),
      sorter: (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
    },
    {
      title: 'Type d\'activité',
      dataIndex: 'activity_type',
      key: 'activity_type',
      width: 200,
      render: (activityType) => (
        <Tag color={getActivityColor(activityType)}>
          {ACTIVITY_LABELS[activityType] || activityType}
        </Tag>
      ),
      filters: Object.entries(ACTIVITY_TYPES).map(([key, value]) => ({
        text: ACTIVITY_LABELS[value] || value,
        value
      })),
      onFilter: (value, record) => record.activity_type === value
    },
    {
      title: 'Catégorie',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (category) => (
        <Tag>{category || 'N/A'}</Tag>
      )
    },
    {
      title: 'Entité',
      dataIndex: 'entity_type',
      key: 'entity_type',
      width: 120
    },
    {
      title: 'ID Entité',
      dataIndex: 'entity_id',
      key: 'entity_id',
      width: 150,
      render: (entityId) => (
        <Tooltip title={entityId}>
          <span>{entityId?.substring(0, 20)}...</span>
        </Tooltip>
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: {
        showTitle: false
      },
      render: (description) => (
        <Tooltip title={description}>
          {description}
        </Tooltip>
      )
    },
    {
      title: 'Utilisateur',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 150
    },
    {
      title: 'IP',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 130
    }
  ];

  return (
    <div className="audit-log-page">
      <Card
        title={
          <span>
            <FilterOutlined style={{ marginRight: 8 }} />
            Journal d'activité - Traçabilité
          </span>
        }
        extra={
          <Space>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExportJSON}
              loading={loading}
            >
              Export JSON
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExportCSV}
              loading={loading}
            >
              Export CSV
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchActivityLogs}
              loading={loading}
            >
              Actualiser
            </Button>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleShowDeleteModal}
              loading={deleteLoading}
            >
              Supprimer par date
            </Button>
          </Space>
        }
      >
        {/* Section Filtres */}
        <Card
          size="small"
          style={{ marginBottom: 24 }}
          title={<FilterOutlined style={{ marginRight: 8 }} />}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <label style={{ display: 'block', marginBottom: 8 }}>Type d'activité</label>
              <Select
                style={{ width: '100%' }}
                placeholder="Tous les types"
                value={filters.activityType}
                onChange={(value) => handleFilterChange('activityType', value)}
                options={[
                  { label: 'Tous les types', value: '' },
                  ...Object.entries(ACTIVITY_TYPES).map(([key, value]) => ({
                    label: ACTIVITY_LABELS[value] || value,
                    value
                  }))
                ]}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ display: 'block', marginBottom: 8 }}>Catégorie</label>
              <Select
                style={{ width: '100%' }}
                placeholder="Toutes les catégories"
                value={filters.category}
                onChange={(value) => handleFilterChange('category', value)}
                options={[
                  { label: 'Toutes les catégories', value: '' },
                  { label: 'Utilisateurs', value: ACTIVITY_CATEGORIES.USER },
                  { label: 'Abonnés', value: ACTIVITY_CATEGORIES.SUBSCRIBER },
                  { label: 'Compteurs', value: ACTIVITY_CATEGORIES.METER },
                  { label: 'Relevés', value: ACTIVITY_CATEGORIES.READING },
                  { label: 'Factures', value: ACTIVITY_CATEGORIES.BILLING },
                  { label: 'Paiements', value: ACTIVITY_CATEGORIES.PAYMENT },
                  { label: 'Rapports', value: ACTIVITY_CATEGORIES.REPORT },
                  { label: 'Système', value: ACTIVITY_CATEGORIES.SYSTEM }
                ]}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ display: 'block', marginBottom: 8 }}>Type d'entité</label>
              <Input
                placeholder="ex: subscriber, meter, payment"
                value={filters.entityType}
                onChange={(e) => handleFilterChange('entityType', e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ display: 'block', marginBottom: 8 }}>Date de début</label>
              <DatePicker
                style={{ width: '100%' }}
                placeholder="Depuis"
                value={filters.startDate}
                onChange={(date) => handleFilterChange('startDate', date)}
                format="DD/MM/YYYY"
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ display: 'block', marginBottom: 8 }}>Date de fin</label>
              <DatePicker
                style={{ width: '100%' }}
                placeholder="Jusqu'au"
                value={filters.endDate}
                onChange={(date) => handleFilterChange('endDate', date)}
                format="DD/MM/YYYY"
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ display: 'block', marginBottom: 8 }}>&nbsp;</label>
              <Button
                block
                icon={<ClearOutlined />}
                onClick={handleResetFilters}
              >
                Réinitialiser
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Statistiques */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Total d'activités"
              value={totalCount}
              suffix="enregistrements"
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Page actuelle"
              value={currentPage}
              suffix={`/ ${Math.ceil(totalCount / pageSize)}`}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Activités par page"
              value={pageSize}
            />
          </Col>
        </Row>

        {/* Tableau des activités */}
        <Spin spinning={loading}>
          {activities.length > 0 ? (
            <Table
              columns={columns}
              dataSource={activities}
              key="timestamp"
              rowKey="id"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalCount,
                onChange: (page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                },
                showSizeChanger: true,
                showTotal: (total) => `Total: ${total} activités`,
                pageSizeOptions: ['10', '20', '50', '100']
              }}
              scroll={{ x: 1200 }}
              size="small"
            />
          ) : (
            <Empty
              description={loading ? 'Chargement...' : 'Aucune activité trouvée'}
              style={{ marginTop: 50, marginBottom: 50 }}
            />
          )}
        </Spin>
      </Card>

      {/* Modal pour suppression par plage de dates */}
      <Modal
        title={
          <span>
            <DeleteOutlined style={{ marginRight: 8, color: '#ff4d4f' }} />
            Supprimer les logs par plage de dates
          </span>
        }
        open={deleteModalVisible}
        onCancel={() => {
          setDeleteModalVisible(false);
          setDeleteStartDate(null);
          setDeleteEndDate(null);
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setDeleteModalVisible(false);
            setDeleteStartDate(null);
            setDeleteEndDate(null);
          }}>
            Annuler
          </Button>,
          <Button
            key="submit"
            danger
            type="primary"
            loading={deleteLoading}
            onClick={handleDeleteActivityLogs}
            disabled={!deleteStartDate || !deleteEndDate}
          >
            Supprimer définitivement
          </Button>
        ]}
        width={600}
      >
        <Alert
          message="ATTENTION: Action irréversible"
          description="Les enregistrements supprimés ne pourront pas être récupérés. Une confirmation supplémentaire vous sera demandée avant la suppression."
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Card size="small" style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>Sélectionner la plage de dates</h3>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <label style={{ display: 'block', marginBottom: 8 }}>
                <strong>Date de début</strong>
              </label>
              <DatePicker
                showTime
                format="DD/MM/YYYY HH:mm:ss"
                style={{ width: '100%' }}
                value={deleteStartDate}
                onChange={setDeleteStartDate}
                placeholder="Depuis"
              />
            </Col>
            <Col xs={24} sm={12}>
              <label style={{ display: 'block', marginBottom: 8 }}>
                <strong>Date de fin</strong>
              </label>
              <DatePicker
                showTime
                format="DD/MM/YYYY HH:mm:ss"
                style={{ width: '100%' }}
                value={deleteEndDate}
                onChange={setDeleteEndDate}
                placeholder="Jusqu'au"
              />
            </Col>
          </Row>
        </Card>

        {deleteStartDate && deleteEndDate && (
          <Card size="small" style={{ backgroundColor: '#fafafa' }}>
            <p style={{ margin: 0 }}>
              <strong>Résumé:</strong> Suppression de <strong>{deleteStartDate.format('DD/MM/YYYY HH:mm:ss')}</strong> à <strong>{deleteEndDate.format('DD/MM/YYYY HH:mm:ss')}</strong>
            </p>
          </Card>
        )}
      </Modal>
    </div>
  );
};

export default AuditLogPage;
